// CMP-Sidebar : navigation principale filtrée par droits. Maquette : 256 px ouverte (72 px réduite), fond surface,
// logo AMR 48 px + « WANGRE », groupes 14 px semi-gras, entrées 38 px, active = fond primaire subtil + barre 3 px.
// Une seule entrée active : la plus précise (« À valider » /depenses/a-valider plutôt que « Dépenses » /depenses).
import { Link, useRouterState } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BrandBlock } from '@/design-system/Brand';
import { cn } from '@/lib/cn';
import type { NavGroup } from '@/navigation/nav-config';

// Pastille des messages non lus (lot F8), chargée à la demande.
const NavUnreadCount = lazy(() => import('@/modules/comms/UnreadIndicators').then((m) => ({ default: m.NavUnreadCount })));

export interface SidebarProps {
  groups: NavGroup[];
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  onNavigate?: () => void;
  className?: string;
}

export function Sidebar({ groups, collapsed = false, onToggleCollapsed, onNavigate, className }: SidebarProps) {
  const { t } = useTranslation('nav');
  const { t: tc } = useTranslation();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const active = activeTarget(groups, pathname);

  return (
    <aside className={cn('flex h-full flex-col gap-[3px] overflow-y-auto border-r border-border bg-surface px-3 py-4 text-small', collapsed ? 'w-[72px]' : 'w-64', className)}>
      <BrandBlock compact={collapsed} />
      <nav aria-label={t('label')} className="flex flex-1 flex-col gap-[3px]">
        {groups.map((group) => (
          <div key={group.label ?? 'main'} className="flex flex-col gap-[3px]">
            {group.label && !collapsed && <span className="px-2.5 pt-2.5 pb-1 text-small font-semibold text-text-2">{t(`groups.${group.label}` as never)}</span>}
            <ul className="m-0 flex list-none flex-col gap-[3px] p-0">
              {group.items.map((entry) => {
                const Icon = entry.icon;
                const label = t(`items.${entry.label}` as never) as string;
                return (
                  <li key={entry.id}>
                    <Link
                      to={entry.to}
                      search={entry.search as never}
                      onClick={onNavigate}
                      title={collapsed ? label : undefined}
                      aria-label={collapsed ? label : undefined}
                      aria-current={entry.to !== undefined && entry.to === active ? 'page' : undefined}
                      className={cn(
                        'flex h-[38px] items-center gap-2.5 rounded-md px-2.5 text-text no-underline hover:bg-surface-2',
                        entry.to !== undefined && entry.to === active && 'bg-primary-subtle font-semibold !text-primary-hover shadow-[inset_3px_0_0_var(--wg-primary)] hover:bg-primary-subtle',
                      )}
                    >
                      <Icon aria-hidden="true" className="size-[19px] shrink-0" />
                      {!collapsed && <span className="flex-1 truncate">{label}</span>}
                      {!collapsed && entry.badge === 'comms' && (
                        <Suspense fallback={null}>
                          <NavUnreadCount />
                        </Suspense>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      {onToggleCollapsed && (
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? tc('actions.expand_menu') : tc('actions.collapse_menu')}
          className="mt-2 flex h-9 cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-text-2 hover:bg-surface-2"
        >
          {collapsed ? <PanelLeftOpen aria-hidden="true" className="size-[19px]" /> : <PanelLeftClose aria-hidden="true" className="size-[19px]" />}
          {!collapsed && tc('actions.collapse_menu')}
        </button>
      )}
    </aside>
  );
}

/** Entrée active : la cible la plus longue qui contient la page courante (ou l'une des pages de sa section). */
export function activeTarget(groups: NavGroup[], pathname: string): string | undefined {
  const within = (prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`);
  return groups
    .flatMap((group) => group.items)
    .flatMap((item) => (item.to === undefined ? [] : [item.to, ...(item.sections ?? [])].filter(within).map((prefix) => ({ to: item.to!, length: prefix.length }))))
    .sort((a, b) => b.length - a.length)[0]?.to;
}
